// hooks/useEnrichedUsersPresence.js
import { useEffect, useRef, useState } from "react";
import { User } from "../../../models/user";
import { useWebSocket } from "../useWebSocket";

// presenceStore internals
const listeners = new Map(); // userId → { handlers: Set, stompSub }
const presenceCache = {}; // userId → { connected, lastSeen }

function subscribeToPresence(userId, callback, client) {
  // first subscriber for this userId?
  if (!listeners.has(userId)) {
    const handlers = new Set<(data: any) => void>();
    // subscribe STOMP
    const stompSub = client.subscribe(`/topic/presence/${userId}`, (msg) => {
      const data = JSON.parse(msg.body);
      presenceCache[userId] = data;
      handlers.forEach((cb) => cb(data));
    });
    listeners.set(userId, { handlers, stompSub });
  }
  // add callback
  listeners.get(userId).handlers.add(callback);
  // immediately notify with cache if exists
  if (presenceCache[userId]) {
    callback(presenceCache[userId]);
  }
}

function unsubscribeFromPresence(userId, callback) {
  const entry = listeners.get(userId);
  if (!entry) return;
  entry.handlers.delete(callback);
  if (entry.handlers.size === 0) {
    // no more local handlers → unsubscribe STOMP
    entry.stompSub.unsubscribe();
    listeners.delete(userId);
    delete presenceCache[userId];
  }
}

export function useEnrichedUsersPresence() {
  const [users, _setUsers] = useState<User[]>([]);
  const subscriptions = useRef(new Map());
  const client = useWebSocket();

  const setUsers = (newUsers: User[]) => {
    const newIds = newUsers.map((u) => u.id);
    const oldIds = Array.from(subscriptions.current.keys());

    // Unsubscribe removed users
    oldIds
      .filter((id) => !newIds.includes(id))
      .forEach((id) => {
        const handler = subscriptions.current.get(id);
        unsubscribeFromPresence(id, handler);
        subscriptions.current.delete(id);
      });

    // Subscribe to new users
    newIds
      .filter((id) => !subscriptions.current.has(id))
      .forEach((id) => {
        const handler = (presence) => {
          _setUsers((cur) =>
            cur.map((u) =>
              u.id === id
                ? {
                    ...u,
                    connected: presence.connected,
                    lastSeen: presence.lastSeen,
                  }
                : u
            )
          );
        };
        subscribeToPresence(id, handler, client);
        subscriptions.current.set(id, handler);
      });

    // Enrich initial array with cached presence or keep initial values
    const enriched = newUsers.map((u) => {
      const p = presenceCache[u.id] || {};
      return {
        ...u,
        connected: p.connected ?? u.connected ?? false, // Keep initial value if present
        lastSeen: p.lastSeen ?? u.lastSeen ?? null, // Keep initial value if present
      };
    });

    _setUsers(enriched);
  };

  // cleanup on unmount
  useEffect(() => {
    const subs = subscriptions.current;
    return () => {
      subs.forEach((handler, id) => {
        unsubscribeFromPresence(id, handler);
      });
      subs.clear();
      // optionally deactivate STOMP:
      // getStompClient().deactivate();
    };
  }, []);

  return [users, setUsers] as const;
}
